import { DropdownListItem, Icon } from '@toggle/design-system';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatMessagePrompt } from '~/components/chat-message-prompt/ChatMessagePrompt';
import { useChat } from '~/stores/use-chat/useChat';
import { generateRandomArray } from '~/utils/common/utils';
import { Messages } from '~/views/intel/messages/Messages';

import * as S from './DocumentAssistant.styles';
import { getQuestions } from './utils';

export type DocumentAssistantOption = {
  label: string;
  documentNames: string[];
};

export interface DocumentAssistantProps {
  defaultOption: string;
  options: DocumentAssistantOption[];
  onClose: () => void;
  subtitle: string;
  headerIcon: ReactNode;
  optionIcon: ReactNode;
  chatPlaceholder?: string;
}

export const DocumentAssistant = ({
  defaultOption,
  options,
  onClose,
  subtitle,
  headerIcon,
  optionIcon,
  chatPlaceholder,
}: DocumentAssistantProps) => {
  const { t } = useTranslation(['widget', 'asset-overview']);
  const [activeOption, setActiveOption] = useState(defaultOption);

  const { messages, sessionId, resetStore, sendMessage } = useChat(state => ({
    messages: state.messages,
    sessionId: state.sessionId,
    resetStore: state.resetStore,
    sendMessage: state.sendMessage,
  }));

  const questions = useMemo(() => {
    const sampleQuestions = getQuestions(t);
    return generateRandomArray(sampleQuestions, 3);
  }, [activeOption]);

  useEffect(() => {
    setActiveOption(defaultOption);

    if (defaultOption) {
      resetStore();
    }
  }, [defaultOption, resetStore]);

  const docNames = options
    .find(option => option.label === activeOption)
    ?.documentNames.join(',');

  const handleMessageClick = (question: string) => {
    const content = `${question}[[${docNames}]]`;
    sendMessage({ question: content, target: 'document-assistant' });
  };

  const isOpen = !!defaultOption;
  const hasMessages = messages.length > 0;

  return (
    <S.StyledDrawer
      data-testid="documents-drawer"
      isOpen={isOpen}
      handleCloseClick={onClose}
    >
      <S.Root>
        {hasMessages ? (
          <S.MessagesWrapper>
            <Messages messages={messages} sessionId={sessionId} />
          </S.MessagesWrapper>
        ) : (
          <S.Content>
            {headerIcon}
            <S.Title>{t('widget:documentAssistant.title')}</S.Title>
            <S.Text>
              {subtitle ?? t('widget:documentAssistant.subtitle')}
            </S.Text>
            <S.QuestionContainer>
              <S.QuestionsHeader>
                {t('widget:documentAssistant.sampleQuestions')}
              </S.QuestionsHeader>
              <S.Questions>
                {questions.map((question, index) => (
                  <ChatMessagePrompt
                    title=""
                    message={question}
                    key={index}
                    onClick={() => handleMessageClick(question)}
                  />
                ))}
              </S.Questions>
            </S.QuestionContainer>
          </S.Content>
        )}
        <S.ChatFooterWrapper>
          <S.StyledDropdown
            selectItem={setActiveOption}
            activeItemIdx={options.findIndex(d => d.label === activeOption)}
            shouldShowItems={false}
            placement="right-end"
            inPortal
            renderDropdownItem={(item, isActive, isFocused) => (
              <DropdownListItem
                label={item}
                isActive={isActive}
                isFocused={isFocused}
                leadingIcon={optionIcon}
              />
            )}
            dropdownItems={options.map(opt => opt.label)}
          >
            <S.SelectorWrapper>
              <S.StyledButton>
                {optionIcon}
                <S.ButtonText>{activeOption}</S.ButtonText>
                <Icon iconName="ChevronLightDown" size={12} />
              </S.StyledButton>
            </S.SelectorWrapper>
          </S.StyledDropdown>
          <S.StyledChatSubmit
            isSimpleVersion
            chatContext={{
              target: 'document-assistant',
              documentName: docNames,
            }}
            placeholder={chatPlaceholder}
          />
        </S.ChatFooterWrapper>
      </S.Root>
    </S.StyledDrawer>
  );
};
